import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Typography,
  Paper,
  Toolbar,
  InputAdornment,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import axios from "axios";
import ActionsMenu from "./ActionsMenu";
import Swal from "sweetalert2";

const initialForm = {
  party_id: "",
  program_id: "",
  advance_amount: "",
  advance_date: "",
  payment_mode: "",
  transaction_no: "",
  purpose: "",
  academic_year: "",
  doc_id: "",
  file: null,
  file_path: "",
};

const formatDateForInput = (value) => {
  if (!value) return "";
  return new Date(value).toISOString().slice(0, 10);
};

export default function Advance() {
  const [rows, setRows] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [parties, setParties] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  // Pagination state
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);

  const user = JSON.parse(sessionStorage.getItem("user"));
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const clientId = user?.client_id;
  const collegeId = user?.college_id;

  const getCurrentAcademicYear = (years) => {
    if (!years || years.length === 0) return "";
    const currentYear = new Date().getFullYear();
    const currentYearMatch = years.find(
      (year) => year.academic_year && year.academic_year.includes(String(currentYear))
    );
    if (currentYearMatch) return currentYearMatch.academic_year;
    return years[0]?.academic_year || "";
  };

  const fetchLookups = async () => {
    try {
      const [programRes, partyRes, academicYearRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/programs/${clientId}/${collegeId}`),
        axios.get(`${API_BASE_URL}/api/adv-exp-party/${clientId}/${collegeId}?page=1&pageSize=500`),
        axios.get(`${API_BASE_URL}/api/acyear/by-college/${clientId}/${collegeId}`),
      ]);
      setPrograms(programRes.data || []);
      setParties(partyRes.data?.data?.items || []);
      const years = academicYearRes.data?.data || [];
      setAcademicYears(years);
      const currentYear = getCurrentAcademicYear(years);
      setForm((prevForm) => ({ ...prevForm, academic_year: currentYear }));
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || "Failed to load dropdown data",
        severity: "error",
      });
    }
  };

  // Fetch advances data (server-side pagination)
  const fetchData = async (page = 0, pageSize = 10, search = "") => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/adv-exp-advance/${clientId}/${collegeId}?page=${
          page + 1
        }&pageSize=${pageSize}&search=${encodeURIComponent(search)}`
      );
      if (data.success) {
        setRows((data.data.items || []).map((r) => ({ id: r.advance_id, ...r })));
        setRowCount(data.data.total || 0);
      } else {
        setSnackbar({
          open: true,
          message: data.message || "Failed to load data",
          severity: "error",
        });
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || "Failed to load data",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId && collegeId && clientId !== "null" && collegeId !== "null") {
      fetchLookups();
    }
    // eslint-disable-next-line
  }, [clientId, collegeId]);

  useEffect(() => {
    if (clientId && collegeId && clientId !== "null" && collegeId !== "null") {
      fetchData(page, pageSize, search);
    }
    // eslint-disable-next-line
  }, [clientId, collegeId, page, pageSize, search]);

  const handleOpen = () => {
    setEditing(null);
    setForm({ ...initialForm, academic_year: getCurrentAcademicYear(academicYears) });
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  const handleEdit = (row) => {
    const document = row.documents?.[0] || {};
    setEditing(row);
    setForm({
      academic_year:
        row.academic_year || document.academic_year || getCurrentAcademicYear(academicYears),
      party_id: row.party_id || "",
      program_id: row.program_id || "",
      advance_amount: row.advance_amount || "",
      advance_date: formatDateForInput(row.advance_date),
      payment_mode: row.payment_mode || "",
      transaction_no: row.transaction_no || "",
      purpose: row.purpose || "",
      doc_id: document.doc_id || "",
      file: null,
      file_path: document.file_path || "",
    });
    setOpen(true);
  };

  const handleDelete = async (row) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This will delete the advance only if no settlements or documents exist.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, keep it!",
    });
    if (!result.isConfirmed) return;
    try {
      await axios.delete(
        `${API_BASE_URL}/api/adv-exp-advance/${clientId}/${collegeId}/${row.advance_id}`
      );
      await Swal.fire("Deleted!", "Your advance has been deleted.", "success");
      fetchData(page, pageSize, search);
    } catch (err) {
      await Swal.fire(
        "Oops!",
        err?.response?.data?.message || "Delete failed. Please try again.",
        "error"
      );
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "application/pdf"];
    if (!allowedTypes.includes(file.type)) {
      setSnackbar({
        open: true,
        message: "Only JPEG, PNG, GIF, and PDF files are allowed!",
        severity: "error",
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setSnackbar({
        open: true,
        message: "File size should be less than 5MB!",
        severity: "error",
      });
      return;
    }

    setForm({ ...form, file });
  };

  const buildAdvancePayload = () => {
    const { doc_id, file, file_path, ...advancePayload } = form;
    return advancePayload;
  };

  const saveAdvanceDocument = async (advanceId, docId = "") => {
    if (!form.file && !docId) return;

    const submitFormData = new FormData();
    submitFormData.append("advance_id", advanceId);
    submitFormData.append("academic_year", form.academic_year);
    submitFormData.append("college_id", collegeId);

    if (form.file) {
      submitFormData.append("file", form.file);
    }

    if (docId) {
      submitFormData.append("updated_by", user?.user_email || "system");
      submitFormData.append("updated_on", new Date().toISOString());
      await axios.put(
        `${API_BASE_URL}/api/adv-exp-advance-documents/${clientId}/${collegeId}/${docId}`,
        submitFormData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      return;
    }

    submitFormData.append("created_by", user?.user_email || "system");
    await axios.post(
      `${API_BASE_URL}/api/adv-exp-advance-documents/${clientId}/${collegeId}`,
      submitFormData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );
  };

  const handleSubmit = async () => {
    if (!form.academic_year) {
      setSnackbar({
        open: true,
        message: "Please select an academic year!",
        severity: "error",
      });
      return;
    }

    if (!editing && !form.file) {
      setSnackbar({
        open: true,
        message: "Please upload an advance document!",
        severity: "error",
      });
      return;
    }

    setUploading(true);
    try {
      const advancePayload = buildAdvancePayload();
      if (editing) {
        await axios.put(
          `${API_BASE_URL}/api/adv-exp-advance/${clientId}/${collegeId}/${editing.advance_id}`,
          {
            ...advancePayload,
            updated_by: user?.user_email || "system",
            updated_on: new Date().toISOString(),
          }
        );
        if (form.file || form.doc_id) {
          await saveAdvanceDocument(editing.advance_id, form.doc_id);
        }
        setSnackbar({ open: true, message: "Advance Updated successfully", severity: "success" });
      } else {
        const { data } = await axios.post(
          `${API_BASE_URL}/api/adv-exp-advance/${clientId}/${collegeId}`,
          {
            ...advancePayload,
            college_id: collegeId,
            created_by: user?.user_email || "system",
          }
        );
        const advanceId = data?.data?.advance_id;
        if (!advanceId) {
          throw new Error("Advance saved but advance id was not returned");
        }
        await saveAdvanceDocument(advanceId);
        setSnackbar({ open: true, message: "Advance Created successfully", severity: "success" });
      }
      setOpen(false);
      fetchData(page, pageSize, search);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || err.message || "Save failed",
        severity: "error",
      });
    } finally {
      setUploading(false);
    }
  };

  const openDocument = (filePath) => {
    if (filePath) {
      window.open(`${API_BASE_URL}${filePath}`, "_blank");
    }
  };

  const columns = [
    {
      field: "serial",
      headerName: "S. No.",
      width: 90,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return params.api.getRowIndex(params.id) + 1;
      },
    },
    {
      field: "advance_date",
      headerName: "Date",
      flex: 0.7,
      renderCell: (params) => formatDateForInput(params.row.advance_date),
    },
    {
      field: "party_name",
      headerName: "Party",
      flex: 1,
      renderCell: (params) => params.row.party?.party_name || "",
    },
    {
      field: "program_name",
      headerName: "Program",
      flex: 1,
      renderCell: (params) => params.row.program?.program_name || "",
    },
    { field: "advance_amount", headerName: "Advance Amount", flex: 0.8 },
    { field: "settled_amount", headerName: "Settled Amount", flex: 0.8 },
    { field: "balance_amount", headerName: "Balance Amount", flex: 0.8 },
    { field: "status", headerName: "Status", flex: 0.7 },
    { field: "payment_mode", headerName: "Payment Mode", flex: 0.8 },
    {
      field: "document",
      headerName: "Document",
      flex: 0.8,
      sortable: false,
      renderCell: (params) => {
        const document = params.row.documents?.[0];
        return document?.file_path ? (
          <Button
            size="small"
            variant="outlined"
            sx={{ color: "#000", borderColor: "#000" }}
            onClick={() => openDocument(document.file_path)}
          >
            View
          </Button>
        ) : (
          ""
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      sortable: false,
      renderCell: (params) => (
        <ActionsMenu row={params.row} onEdit={handleEdit} onDelete={handleDelete} />
      ),
    },
  ];

  return (
    <Box p={2}>
      <Paper elevation={3} sx={{ p: 2 }}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Advances
          </Typography>
          <TextField
            size="small"
            placeholder="Search..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mr: 2 }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpen}
            sx={{ color: "#fff" }}
          >
            Add Advance
          </Button>
        </Toolbar>
        <Box height={400} mt={2}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            page={page}
            pageSize={pageSize}
            rowCount={rowCount}
            pagination
            paginationMode="server"
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
            disableSelectionOnClick
            autoHeight
          />
        </Box>
      </Paper>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? "Edit Advance" : "Add Advance"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              select
              margin="normal"
              label="Academic Year"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.academic_year}
              onChange={(e) => setForm({ ...form, academic_year: e.target.value })}
              SelectProps={{ native: true }}
              required
            >
              <option value="">Select Academic Year</option>
              {academicYears.map((year) => (
                <option key={year.acyear_id} value={year.academic_year}>
                  {year.academic_year}
                </option>
              ))}
            </TextField>
            <TextField
              select
              margin="normal"
              label="Party"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.party_id}
              onChange={(e) => setForm({ ...form, party_id: e.target.value })}
              SelectProps={{ native: true }}
              required
            >
              <option value="">Select Party</option>
              {parties.map((party) => (
                <option key={party.party_id} value={party.party_id}>
                  {party.party_name}
                </option>
              ))}
            </TextField>
            <TextField
              select
              margin="normal"
              label="Program"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.program_id}
              onChange={(e) => setForm({ ...form, program_id: e.target.value })}
              SelectProps={{ native: true }}
            >
              <option value="">None</option>
              {programs.map((program) => (
                <option key={program.program_id} value={program.program_id}>
                  {program.program_name}
                </option>
              ))}
            </TextField>
            <TextField
              label="Advance Amount"
              value={form.advance_amount}
              onChange={(e) => setForm({ ...form, advance_amount: e.target.value })}
              type="number"
              fullWidth
              required
            />
            <TextField
              label="Advance Date"
              value={form.advance_date}
              onChange={(e) => setForm({ ...form, advance_date: e.target.value })}
              type="date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            />
            <TextField
              select
              margin="normal"
              label="Payment Mode"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.payment_mode}
              onChange={(e) => setForm({ ...form, payment_mode: e.target.value })}
              SelectProps={{ native: true }}
            >
              <option value="">None</option>
              <option value="CASH">CASH</option>
              <option value="BANK">BANK</option>
              <option value="UPI">UPI</option>
              <option value="CHEQUE">CHEQUE</option>
            </TextField>
            <TextField
              label="Transaction No"
              value={form.transaction_no}
              onChange={(e) => setForm({ ...form, transaction_no: e.target.value })}
              fullWidth
            />
            <TextField
              label="Purpose"
              value={form.purpose}
              onChange={(e) => setForm({ ...form, purpose: e.target.value })}
              fullWidth
              multiline
              minRows={2}
            />
            <Button
              component="label"
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              fullWidth
              sx={{ color: "#000", borderColor: "#000" }}
            >
              {form.file
                ? form.file.name
                : form.file_path
                ? "Replace Advance Document"
                : "Upload Advance Document (JPEG, PNG, GIF, PDF)"}
              <input
                type="file"
                hidden
                accept="image/jpeg,image/jpg,image/png,image/gif,application/pdf"
                onChange={handleFileChange}
              />
            </Button>
            {form.file_path && !form.file && (
              <Button
                size="small"
                variant="outlined"
                sx={{ color: "#000", borderColor: "#000", alignSelf: "flex-start" }}
                onClick={() => openDocument(form.file_path)}
              >
                View Existing Document
              </Button>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{ color: "#fff" }}
            disabled={uploading}
          >
            {uploading ? <CircularProgress size={24} /> : editing ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
